import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { apiClient, useAuth } from "../App";
import { Link, useNavigate } from "react-router-dom";
import { ShieldCheck, Download, Trash2, AlertTriangle, CheckCircle2, FileJson } from "lucide-react";

const MyData = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [consents, setConsents] = useState({
    notifications: !!user?.consent_notifications,
    data_sharing: !!user?.consent_data_sharing,
  });
  const [savingConsents, setSavingConsents] = useState(false);
  const [confirmEmail, setConfirmEmail] = useState("");
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    // Refresh consents from server
    apiClient.get("/auth/me").then((res) => {
      setConsents({
        notifications: !!res.data?.consent_notifications,
        data_sharing: !!res.data?.consent_data_sharing,
      });
    }).catch(() => {});
  }, []);

  const handleExport = async () => {
    try {
      const res = await apiClient.get("/me/data", { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `vivo-my-data-${(user?.email || "user").replace("@", "-at-")}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Your data has been downloaded");
    } catch (e) {
      toast.error("Failed to export your data");
    }
  };

  const handleSaveConsents = async () => {
    setSavingConsents(true);
    try {
      await apiClient.put("/me/consents", {
        consent_notifications: consents.notifications,
        consent_data_sharing: consents.data_sharing,
      });
      toast.success("Preferences saved");
    } catch (e) {
      toast.error("Failed to save");
    } finally {
      setSavingConsents(false);
    }
  };

  const handleDelete = async () => {
    if (confirmEmail.trim().toLowerCase() !== (user?.email || "").toLowerCase()) {
      toast.error("Email doesn't match — type it exactly to confirm");
      return;
    }
    if (!window.confirm("This permanently deletes your account, profile, attendance records and push subscriptions. This action cannot be undone. Continue?")) return;
    setDeleting(true);
    try {
      await apiClient.delete("/me/account");
      toast.success("Your account has been deleted");
      logout();
      navigate("/login");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to delete account");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="space-y-6 animate-fade-in max-w-3xl" data-testid="my-data-page">
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center">
          <ShieldCheck className="w-5 h-5 text-primary" />
        </div>
        <div>
          <h1 className="font-heading text-4xl tracking-tight">My Data &amp; Privacy</h1>
          <p className="text-muted-foreground mt-1">Your UK GDPR rights in one place.</p>
        </div>
      </div>

      {/* Access / Portability */}
      <Card className="glass-card">
        <CardHeader className="pb-3 border-b border-white/5">
          <CardTitle className="flex items-center gap-2"><FileJson className="w-5 h-5 text-primary" /> Download your data</CardTitle>
        </CardHeader>
        <CardContent className="p-6 space-y-3">
          <p className="text-sm text-muted-foreground">
            Get a machine-readable JSON file containing your user account, every player profile, attendance records, dep invitations, and push subscriptions linked to you. You can use this for your own records or to import into another service.
          </p>
          <Button onClick={handleExport} data-testid="export-data-btn">
            <Download className="w-4 h-4 mr-2" /> Export my data
          </Button>
        </CardContent>
      </Card>

      {/* Rectification */}
      <Card className="glass-card">
        <CardHeader className="pb-3 border-b border-white/5">
          <CardTitle className="flex items-center gap-2"><CheckCircle2 className="w-5 h-5 text-primary" /> Correct your data</CardTitle>
        </CardHeader>
        <CardContent className="p-6 space-y-3 text-sm text-muted-foreground">
          <p>Most of your data can be edited by you or a band admin:</p>
          <ul className="list-disc ml-5 space-y-1">
            <li>Name, email, password — <Link to="/profile" className="text-primary underline underline-offset-2">Profile</Link></li>
            <li>Phone, section, emergency contact, uniform sizes, medical/dietary — ask your band admin from the Players page</li>
            <li>Anything else — email <a className="text-primary" href="mailto:james@support.vivomusicianmanagement.co.uk">james@support.vivomusicianmanagement.co.uk</a></li>
          </ul>
        </CardContent>
      </Card>

      {/* Consents */}
      <Card className="glass-card">
        <CardHeader className="pb-3 border-b border-white/5">
          <CardTitle>Consent preferences</CardTitle>
        </CardHeader>
        <CardContent className="p-6 space-y-4">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={consents.notifications}
              onChange={(e) => setConsents({ ...consents, notifications: e.target.checked })}
              className="w-4 h-4 mt-1 accent-primary"
              data-testid="consent-notifications"
            />
            <div>
              <div className="text-sm font-medium">Event reminder emails &amp; push notifications</div>
              <div className="text-xs text-muted-foreground mt-0.5">We&apos;ll remind you about upcoming rehearsals and concerts. You can turn this off anytime.</div>
            </div>
          </label>
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={consents.data_sharing}
              onChange={(e) => setConsents({ ...consents, data_sharing: e.target.checked })}
              className="w-4 h-4 mt-1 accent-primary"
              data-testid="consent-data-sharing"
            />
            <div>
              <div className="text-sm font-medium">Share my contact info with band admins</div>
              <div className="text-xs text-muted-foreground mt-0.5">Required for your band admins to administer your membership (attendance, rotas, concert fees, dep payments).</div>
            </div>
          </label>
          <Button onClick={handleSaveConsents} disabled={savingConsents} data-testid="save-consents-btn">
            {savingConsents ? "Saving..." : "Save preferences"}
          </Button>
        </CardContent>
      </Card>

      {/* Danger zone */}
      <Card className="glass-card border-rose-500/30">
        <CardHeader className="pb-3 border-b border-white/5">
          <CardTitle className="flex items-center gap-2 text-rose-400">
            <AlertTriangle className="w-5 h-5" /> Danger zone — delete my account
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6 space-y-3">
          <p className="text-sm text-muted-foreground">
            Permanently deletes your user account, every linked player profile across every band, all attendance records, dep invitations and push subscriptions. This is your UK GDPR right to erasure and cannot be undone. If you are the sole admin of a band you&apos;ll need to promote another admin first, or delete the band from its Settings page.
          </p>
          <div className="space-y-2">
            <Label>Type <code className="text-xs bg-white/5 px-1.5 py-0.5 rounded">{user?.email}</code> to confirm</Label>
            <Input
              value={confirmEmail}
              onChange={(e) => setConfirmEmail(e.target.value)}
              placeholder={user?.email}
              data-testid="confirm-email-input"
            />
          </div>
          <Button
            variant="destructive"
            onClick={handleDelete}
            disabled={deleting || confirmEmail.trim().toLowerCase() !== (user?.email || "").toLowerCase()}
            data-testid="delete-account-btn"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            {deleting ? "Deleting..." : "Permanently delete my account"}
          </Button>
        </CardContent>
      </Card>

      <div className="text-xs text-muted-foreground pt-4">
        Full details in our <Link to="/privacy" className="text-primary underline underline-offset-2">Privacy Policy</Link>. Data controller: James Fiddeman · <a className="text-primary" href="mailto:james@support.vivomusicianmanagement.co.uk">james@support.vivomusicianmanagement.co.uk</a>
      </div>
    </div>
  );
};

export default MyData;
